import { ALL_BRICK_BASE_KEYS } from "../objects/brickAppearance.js";
import { BRICK_SHATTER_IMAGE_KEYS } from "../objects/brickShatterMeta.generated.js";

/**
 * 발사 섬광 텍스처 variant — fireOnce가 pickMuzzleFlash로 발사마다 랜덤 1종 선택 + 랜덤 회전(단조로움 완화).
 * cross는 테마 고유(ASSETS), burst/star/spike는 양테마 공용 흰 실루엣(SHARED_ASSETS). 키 추가 시 양 테마
 * data/themeAssets.tsv 동시 수정 + generate-theme-minimal.mjs variant 추가 필요.
 */
export const MUZZLE_FLASH_KEYS = [
  "muzzle_flash_white",
  "muzzle_flash_burst",
  "muzzle_flash_star",
  "muzzle_flash_spike",
] as const;

/** 챕터 진행 월드별 HUD/게임뷰/벽돌 PNG 프리셋 키. 파일 경로는 themeAssets_<theme>.tsv가 소유한다. */
export const WORLD_THEME_IMAGE_KEYS = [
  "icon_world_circuit",
  "icon_world_space",
  "icon_world_crystal",
  "icon_world_forge",
  "icon_world_verdant",
  "icon_world_void",
  "world_circuit_bg_tile",
  "world_circuit_brick",
  "world_circuit_brick_v1",
  "world_circuit_brick_v2",
  "world_circuit_brick_square",
  "world_circuit_brick_square_v1",
  "world_circuit_brick_square_v2",
  "world_circuit_brick_wide",
  "world_circuit_brick_wide_v1",
  "world_circuit_brick_wide_v2",
  "world_circuit_brick_gold",
  "world_circuit_brick_mystery",
  "world_circuit_brick_overlay",
  "world_circuit_brick_overlay_v1",
  "world_circuit_brick_overlay_v2",
  "world_circuit_brick_overlay_square",
  "world_circuit_brick_overlay_square_v1",
  "world_circuit_brick_overlay_square_v2",
  "world_circuit_brick_overlay_wide",
  "world_circuit_brick_overlay_wide_v1",
  "world_circuit_brick_overlay_wide_v2",
  "world_space_bg_tile",
  "world_space_brick",
  "world_space_brick_v1",
  "world_space_brick_v2",
  "world_space_brick_v3",
  "world_space_brick_mystery",
  "world_space_brick_overlay",
  "world_space_brick_overlay_v1",
  "world_space_brick_overlay_v2",
  "world_space_brick_overlay_v3",
  "world_crystal_bg_tile",
  "world_crystal_brick",
  "world_crystal_brick_gold",
  "world_crystal_brick_mystery",
  "world_crystal_brick_overlay",
  "icon_world_wormhole",
  "world_wormhole_bg_tile",
  "world_wormhole_brick",
  "world_wormhole_brick_v1",
  "world_wormhole_brick_v2",
  "world_wormhole_brick_v3",
  "world_wormhole_brick_overlay",
  "world_wormhole_brick_overlay_v1",
  "world_wormhole_brick_overlay_v2",
  "world_wormhole_brick_overlay_v3",
  "world_forge_bg_tile",
  "world_forge_brick",
  "world_forge_brick_gold",
  "world_forge_brick_mystery",
  "world_forge_brick_overlay",
  "world_verdant_bg_tile",
  "world_verdant_brick",
  "world_verdant_brick_gold",
  "world_verdant_brick_mystery",
  "world_verdant_brick_overlay",
  "world_void_bg_tile",
  "world_void_brick",
  "world_void_brick_gold",
  "world_void_brick_mystery",
  "world_void_brick_overlay",
] as const;

export const DIALOGUE_PORTRAIT_CAPTAIN_KEY = "dialogue_portrait_captain";
export const DIALOGUE_PORTRAIT_COMMAND_KEY = "dialogue_portrait_command";
export const DIALOGUE_PORTRAIT_OPERATION_LOG_KEY =
  "dialogue_portrait_operation_log";

export const DIALOGUE_PORTRAIT_IMAGE_KEYS = [
  DIALOGUE_PORTRAIT_CAPTAIN_KEY,
  DIALOGUE_PORTRAIT_COMMAND_KEY,
  DIALOGUE_PORTRAIT_OPERATION_LOG_KEY,
] as const;

/**
 * 앱이 요구하는 테마 이미지 텍스처 키 SSoT — 키는 테마 중립(의미 단위)이고 파일 경로는 테마 소유
 * (data/themeAssets.tsv → generated JSON). 매니페스트가 buildManifestAssets에 이 배열을 넘겨
 * 테이블과의 1:1 완전 일치를 모듈 로드 시 throw로 강제한다(누락/오타 = 부팅·테스트 실패).
 * 키 추가 시 여기 + data/themeAssets.tsv(usedIn 사용처 포함) 동시 수정 후 npm run export:data.
 */
export const THEME_IMAGE_KEYS = [
  // HUD 상단바 (9-slice + 재화/지표/설정 아이콘)
  "hud_topbar_bg",
  "hud_currency_chip",
  "hud_icon_button",
  "toast_panel_9slice",
  "icon_gold",
  "icon_diamond",
  "icon_stage",
  ...WORLD_THEME_IMAGE_KEYS,
  "icon_dps",
  "icon_upgrade_stone",
  "icon_rebirth_point",
  "icon_settings",
  // 다이어로그 화자 초상화 — BattleDialogueOverlay가 speaker별 textureKey로 참조한다.
  ...DIALOGUE_PORTRAIT_IMAGE_KEYS,
  // 설정 모달 아이콘 — 계정/문의/외부 스토어 버튼 + 항목 라벨 4종(언어/표기형식/FPS/사운드). 양 테마 공용 흰 실루엣.
  "icon_account_link",
  "icon_account_delete",
  "icon_contact",
  "icon_other_games",
  "icon_opt_language",
  "icon_opt_format",
  "icon_opt_fps",
  "icon_opt_sound",
  // 스탯 업그레이드 행 아이콘 (StatUpgradeTabView·RebirthTabView 좌측 소형 아이콘)
  "icon_stat_attack",
  "icon_stat_fire_rate",
  "icon_stat_gold_gain",
  "icon_stat_crit_chance",
  "icon_stat_crit_damage",
  "icon_stat_point_bonus",
  // 상점 행 아이콘 (ShopTabView 광고 제거 행 좌측)
  "icon_no_ads",
  // 상점 광고 다이아 카드 구매 버튼 아이콘 (shopAd.json buttonIconKey, themeAssets icon_ad 행)
  "icon_ad",
  // 유물 카드/상세 아이콘 — relics.tsv relicId와 1:1 매칭
  "icon_relic_ember_core",
  "icon_relic_berserker_fang",
  "icon_relic_gold_chalice",
  "icon_relic_merchant_seal",
  "icon_relic_chrono_gear",
  "icon_relic_rapid_coil",
  "icon_relic_wind_runner",
  "icon_relic_swift_fletching",
  // GameView 전투 영역 (배경·프레임·블록 룬·발사체·발사대·이펙트)
  "gameview_bg_tile",
  "gameview_frame_9slice",
  "brick_rune_base",
  "projectile_basic_white",
  "projectile_pioneer_bolt",
  "projectile_ranger_lance",
  "projectile_helios_plasma",
  "projectile_xenoframe_shard",
  "projectile_astral_star",
  "launcher_base",
  "launcher_barrel",
  "spaceship_launcher",
  "spaceship_large",
  "spaceship_pioneer_launcher",
  "spaceship_pioneer_large",
  "spaceship_ranger_launcher",
  "spaceship_ranger_large",
  "spaceship_helios_launcher",
  "spaceship_helios_large",
  "spaceship_xenoframe_launcher",
  "spaceship_xenoframe_large",
  "spaceship_astral_launcher",
  "spaceship_astral_large",
  ...MUZZLE_FLASH_KEYS,
  "hit_spark_white",
  "target_marker",
  "icon_mystery_chest",
  // 하단 탭바/콘텐츠 패널 (9-slice + 탭 아이콘 — active 컬러 + inactive 모노크롬)
  "tab_button_inactive_9slice",
  "tab_button_active_9slice",
  "tab_content_panel_9slice",
  // 탭 active (컬러)
  "icon_tab_battle",
  "icon_tab_stat",
  "icon_tab_dungeon",
  "icon_tab_rebirth",
  "icon_tab_shop",
  "icon_tab_daily_shop",
  "icon_tab_codex",
  "icon_tab_relic",
  "icon_tab_spaceship",
  "icon_tab_merge",
  "icon_tab_mastery",
  "icon_tab_record",
  "icon_tab_world",
  // 탭 inactive (모노크롬)
  "icon_tab_battle_inactive",
  "icon_tab_stat_inactive",
  "icon_tab_dungeon_inactive",
  "icon_tab_rebirth_inactive",
  "icon_tab_shop_inactive",
  "icon_tab_daily_shop_inactive",
  "icon_tab_codex_inactive",
  "icon_tab_relic_inactive",
  "icon_tab_spaceship_inactive",
  "icon_tab_merge_inactive",
  "icon_tab_mastery_inactive",
  "icon_tab_record_inactive",
  "icon_tab_world_inactive",
  // 서브탭 아이콘 (active 컬러 + inactive 모노크롬)
  "icon_subtab_codex_modules",
  "icon_subtab_codex_modules_inactive",
  "icon_subtab_codex_synergy",
  "icon_subtab_codex_synergy_inactive",
  "icon_subtab_codex_all",
  "icon_subtab_codex_all_inactive",
  "icon_subtab_codex_base",
  "icon_subtab_codex_base_inactive",
  "icon_subtab_codex_ship",
  "icon_subtab_codex_ship_inactive",
  "icon_subtab_merge",
  "icon_subtab_merge_inactive",
  "icon_subtab_fusion",
  "icon_subtab_fusion_inactive",
  "icon_subtab_shop",
  "icon_subtab_shop_inactive",
  "icon_subtab_daily_shop",
  "icon_subtab_daily_shop_inactive",
  // 숙련도 분기 선택지 아이콘 — ModuleMasteryTabView의 선택 전 효과 비교 라인.
  "icon_mastery_effect_stable",
  "icon_mastery_effect_boost",
  "icon_mastery_branch_spread",
  "icon_mastery_branch_focus",
  // 전투 그리드/벽돌 기능 아이콘 (3초 가독 — 흰 실루엣, 런타임 tint. 양 테마 공용 생성)
  "icon_grid_start",
  "icon_grid_fire",
  "icon_grid_lock",
  "brick_shard_sliver",
  "brick_shard_chunk",
  "brick_shard_tri",
  "brick_shard_splinter",
  // 모듈 효과 아이콘 — 키 관례 icon_effect_<definitionId> (shapeRenderer.effectIconKey가 조회)
  "icon_effect_launcher",
  "icon_effect_double",
  "icon_effect_triple",
  "icon_effect_split",
  "icon_effect_fire",
  "icon_effect_electric",
  "icon_effect_ice",
  "icon_effect_straight",
  "icon_effect_bounce",
  "icon_effect_homing",
  "icon_effect_explosion",
  "icon_effect_pierce",
  "icon_effect_chain",
  "icon_effect_burst",
  "icon_effect_crit",
  "icon_effect_greed",
  "icon_effect_mark",
  "icon_effect_speed",
  "icon_effect_amplify",
  "icon_effect_catalyst",
  "icon_effect_precision",
  "icon_effect_execute",
  "icon_effect_shockwave",
  "icon_effect_aftershock",
  "icon_effect_echoPrev",
  "icon_effect_echoNext",
  "icon_effect_power",
  "icon_effect_rapid",
  // 우주선별 시작 모듈 아이콘 — spaceships.tsv launcherIconKey가 참조한다.
  "icon_launcher_pioneer_scrap",
  "icon_launcher_ranger_mining",
  "icon_launcher_helios_fusion",
  "icon_launcher_xenoframe_shard",
  "icon_launcher_astral_starfold",
  // 튜토리얼 손가락 포인터 — TutorialOverlay 드래그/탭 연출(흰 실루엣 + tint)
  "tutorial_hand",
  // 벽돌 균열 variant 4단계 + 파괴 조각 — 생성기 emit 메타가 키 SSoT (brickShatterMeta.generated를 spread,
  // ALL_BRICK_BASE_KEYS 선례). 수기 이중 등록 없음 — 생성기 재실행이 키·PNG·메타를 원자적으로 갱신한다.
  ...BRICK_SHATTER_IMAGE_KEYS,
] as const;

export type ThemeImageKey = (typeof THEME_IMAGE_KEYS)[number];

const THEME_IMAGE_KEY_SET: ReadonlySet<string> = new Set(THEME_IMAGE_KEYS);

/**
 * 모듈 정의 id → 기능 아이콘 텍스처 키 (관례 `icon_effect_<definitionId>`) — 순수.
 * 키 SSoT(본 파일)에 등록된 경우만 반환, 미등록 id는 null(호출자가 effectAbbr 텍스트 fallback).
 * 신규 모듈 추가 시 아이콘 누락은 moduleIcons.test.ts 게이트가 차단한다.
 * (themes 레이어 소유 — ui가 키 집합을 직접 들추지 않도록 여기서 판정한다.)
 */
export function effectIconKey(definitionId: string): ThemeImageKey | null {
  const key = `icon_effect_${definitionId}`;
  return THEME_IMAGE_KEY_SET.has(key) ? (key as ThemeImageKey) : null;
}

const RELIC_ICON_KEYS: Readonly<Record<string, ThemeImageKey>> = {
  emberCore: "icon_relic_ember_core",
  berserkerFang: "icon_relic_berserker_fang",
  goldChalice: "icon_relic_gold_chalice",
  merchantSeal: "icon_relic_merchant_seal",
  chronoGear: "icon_relic_chrono_gear",
  rapidCoil: "icon_relic_rapid_coil",
  windRunner: "icon_relic_wind_runner",
  swiftFletching: "icon_relic_swift_fletching",
};

/** 유물 정의 id → 유물 전용 아이콘 텍스처 키. 미등록 id는 null(호출자가 generic relic fallback). */
export function relicIconKey(relicId: string): ThemeImageKey | null {
  return RELIC_ICON_KEYS[relicId] ?? null;
}

/**
 * 절차 생성(또는 PNG 대체) 텍스처 키 SSoT — 벽돌 base 키는 brickAppearance(ALL_BRICK_BASE_KEYS)와
 * 동일 SSoT를 spread해 두 곳이 어긋날 수 없다. 테마가 실 에셋으로 전환할 땐 data/themeAssets.tsv에서
 * 해당 행의 kind를 `file`로 바꾸고 path를 채우면 된다(코드 수정 0건).
 */
export const THEME_PROCEDURAL_KEYS = [
  ...ALL_BRICK_BASE_KEYS,
  "ball",
  "px",
  "ui_btn",
  "ui_btn_on",
  "ui_panel",
] as const;

export type ThemeProceduralKey = (typeof THEME_PROCEDURAL_KEYS)[number];
